/**
 * InstructionListView -- Catálogo nexo_catalogs.n8n_instructions (agrupado por instruction_id).
 *
 * @module presentation/views/InstructionListView
 */

import { InstructionApiClient } from '../../infrastructure/api/InstructionApiClient.js';

const STATUS_COLORS = {
  active: COLOR_ESTADO.ok,
  archived: COLOR_ESTADO.apagado
};

// Deuda 4: este escape (no cambiaba la comilla simple) es ahora el compartido.
import { esc } from '../shared/texto.js';
import { insigniaHtml, COLOR_ESTADO } from '../shared/insignia.js';
import { leerLectura, resumen, estadosPorCodigo } from '../../application/instrucciones/lecturaDelAgente.js';
import { regresoHtml } from '../shared/regreso.js';

// INSTRUCCIONES-FUENTE-UNICA-T1 paso 3: el estado de Git de cada codigo de esta lista (n8n_instructions), de la API.
let estadosN8n = new Map();

function chipDeGit(code) {
  const f = estadosN8n.get(code);
  if (!f) return '';
  const e = f.estado;
  return insigniaHtml(esc(e.etiqueta), e.color, `data-git-estado="${esc(e.clave)}" title="${esc(e.ayuda + (f.rutaGit ? ' ' + f.rutaGit : ''))}"`);
}

/** Arriba de la lista: cuantas coinciden, difieren o no tienen fuente, y la lista completa plegada. */
function lecturaHtml(l) {
  if (!l.ok) return `<p class="empty-state" data-lectura-motivo="${esc(l.motivo)}" style="margin:0">${esc(l.frase)}</p>`;
  const r = resumen(l.filas);
  // Q #37538: estado_git compara VERSIONES (no textos): las cifras dicen version, no "coincide el texto".
  const cifras = (x) => `${x.coincide} con la misma versión que Git · ${x.difiere} con otra versión · ${x.sin_fuente} sin fuente`;
  const almacenes = Object.entries(r.porAlmacen).map(([a, x]) =>
    `<li data-lectura-almacen="${esc(a)}"><code>${esc(a)}</code>: ${x.total} · ${esc(cifras(x))}</li>`).join('');
  const filas = l.filas.map((f) => `
    <tr data-lectura-codigo="${esc(f.codigo)}"><td><code>${esc(f.codigo)}</code></td><td>${esc(f.almacen)}</td>
      <td>${esc(f.version || '—')}${f.versionGit ? ` · Git ${esc(f.versionGit)}` : ''}</td>
      <td><span style="color:${f.estado.color}">${esc(f.estado.etiqueta)}</span></td>
      <td data-lectura-lee="${f.textoCompleto ? 'completo' : 'resumen'}">${f.textoCompleto ? 'texto completo' : 'resumen'}</td>
      <td style="word-break:break-all">${esc(f.rutaGit || '—')}</td></tr>`).join('');
  return `
    <p style="margin:0;font-size:0.875rem;color:var(--text-heading)" data-lectura-total>Lo que lee un agente: <strong>${r.total.total}</strong> instrucciones · ${esc(cifras(r.total))}</p>
    <ul style="margin:6px 0 0;padding-left:18px;font-size:0.875rem;color:var(--text-secondary)">${almacenes}</ul>
    <p class="fp-nada" style="margin:6px 0 0;font-size:0.875rem;color:var(--text-secondary)" data-lectura-que-mide>El estado compara la versión registrada en la base con la del documento en Git; no compara textos. De una instrucción INS el agente lee un resumen, no el documento.</p>
    <details style="margin-top:6px"><summary style="cursor:pointer;font-size:0.875rem;color:var(--accent-blue)">Ver la lista completa (incluye lo que leen los agentes por MCP)</summary>
      <div style="overflow-x:auto"><table class="install-table" style="margin-top:6px"><thead><tr><th>Código</th><th>Almacén</th><th>Versión</th><th>Git</th><th>El agente lee</th><th>Documento</th></tr></thead><tbody>${filas}</tbody></table></div>
    </details>`;
}

async function cargarLectura() {
  const $l = document.getElementById('nx-lectura-agente');
  const l = leerLectura(await InstructionApiClient.lectura());
  estadosN8n = l.ok ? estadosPorCodigo(l.filas, 'n8n_instructions') : new Map();
  if ($l) $l.innerHTML = lecturaHtml(l);
  // Las filas ya pintadas reciben su chip sin volver a pedir la lista.
  document.querySelectorAll('#nx-inst-list .instruction-row').forEach(($r) => {
    const $acc = $r.querySelector('.task-actions');
    if ($acc && !$acc.querySelector('[data-git-estado]')) $acc.insertAdjacentHTML('afterbegin', chipDeGit($r.dataset.code));
  });
}

function badge(status) {
  const color = STATUS_COLORS[status] || COLOR_ESTADO.apagado;
  return insigniaHtml(esc(status), color);
}

function instructionRow(inst) {
  const ver = inst.instructionVersion ? ` · v${inst.instructionVersion}` : '';
  const steps = typeof inst.stepCount === 'number' ? ` · ${inst.stepCount} paso(s)` : '';
  const bodyPreview = inst.body?.substring(0, 100)?.replace(/\n/g, ' ') || '';
  return `
    <div class="artifact-item instruction-row" data-id="${esc(inst.id)}" data-code="${esc(inst.code || '')}" role="link" tabindex="0"
      style="cursor:pointer" title="Abrir detalle">
      <div class="task-info">
        <div class="artifact-item-title">
          <code class="instruction-code">${esc(inst.code || '')}</code> ${esc(inst.title || '(sin titulo)')}
        </div>
        <div class="artifact-item-meta">${esc(bodyPreview)}${bodyPreview.length >= 100 ? '…' : ''}</div>
        <div class="artifact-item-meta" style="font-size:0.875rem;color:var(--accent-blue)">${esc(ver + steps)}</div>
      </div>
      <div class="task-actions">
        ${chipDeGit(inst.code)}
        ${inst.area ? `<span class="area-tag">${esc(inst.area)}</span>` : ''}
        ${badge(inst.status)}
      </div>
    </div>`;
}

export async function renderInstructionList(container) {
  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>Instrucciones</h1>
          <p class="subtitle">Catálogo <code>nexo_catalogs.n8n_instructions</code> (n8n) · una entrada por <code>instruction_id</code></p>
        </div>
        <button class="btn-primary btn-sm" id="btn-new-instruction">+ Nueva instruccion</button>
      </div>

      <div id="nx-inst-form-area" hidden>
        <form id="nx-inst-form" class="inline-form inline-form-col" style="max-width:640px">
          <p style="font-size:0.875rem;color:var(--text-secondary);margin:0 0 0.75rem 0">Crea el primer paso. Código = <code>instruction_id</code> (ej. INS-MI-001).</p>
          <div class="form-row">
            <input type="text" id="nx-inst-code" placeholder="instruction_id (ej: INS-FONDO-002)" required class="form-input form-input-sm">
            <input type="text" id="nx-inst-title" placeholder="Titulo" required class="form-input">
          </div>
          <div class="form-row">
            <input type="text" id="nx-inst-version" placeholder="Version (1.0.0)" class="form-input form-input-sm">
            <input type="text" id="nx-inst-area" placeholder="Area / target_product" class="form-input form-input-sm">
            <input type="text" id="nx-inst-param" placeholder="param_name (default: main)" class="form-input form-input-sm">
          </div>
          <textarea id="nx-inst-desc" placeholder="Descripcion (columna description del paso)" rows="3" class="form-input form-textarea"></textarea>
          <textarea id="nx-inst-vars" placeholder='variables_schema JSON (opcional) — ej: {"prompt_template":"..."}' rows="8" class="form-input form-textarea" style="font-family:ui-monospace,monospace;font-size:0.875rem"></textarea>
          <textarea id="nx-inst-sql" placeholder="sql_query del paso (opcional si solo hay JSON)" rows="3" class="form-input form-textarea" style="font-family:ui-monospace,monospace;font-size:0.875rem"></textarea>
          <div class="form-row">
            <button type="submit" class="btn-primary btn-sm">Crear</button>
            <button type="button" class="btn-ghost btn-sm" id="btn-cancel-inst">Cancelar</button>
          </div>
        </form>
        <p id="nx-inst-form-error" class="auth-error" hidden></p>
      </div>

      <div id="nx-lectura-agente" style="margin:0 0 12px;padding:10px 12px;background:var(--bg-secondary);border:1px solid var(--border-default);border-radius:8px">
        <p class="empty-state" style="margin:0">Cargando lo que lee un agente…</p>
      </div>

      <div class="artifact-list" id="nx-inst-list">
        <p class="empty-state">Cargando instrucciones...</p>
      </div>
      <div id="nx-inst-pager" class="pager"></div>
    </section>`;

  setupFormHandlers();
  setupListClickDelegation(container);
  estadosN8n = new Map();
  await Promise.all([loadInstructions(1), cargarLectura()]);
}

function setupListClickDelegation(container) {
  container.addEventListener('click', (e) => {
    const row = e.target.closest('.instruction-row');
    if (!row?.dataset?.id) return;
    window.location.hash = `#/instructions/${row.dataset.id}`;
  });
  container.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const row = e.target.closest('.instruction-row');
    if (!row?.dataset?.id) return;
    window.location.hash = `#/instructions/${row.dataset.id}`;
  });
}

function setupFormHandlers() {
  const $btn = document.getElementById('btn-new-instruction');
  const $area = document.getElementById('nx-inst-form-area');
  const $form = document.getElementById('nx-inst-form');
  const $cancel = document.getElementById('btn-cancel-inst');
  const $error = document.getElementById('nx-inst-form-error');

  $btn.onclick = () => {
    $area.hidden = !$area.hidden;
    if (!$area.hidden) document.getElementById('nx-inst-code').focus();
  };

  $cancel.onclick = () => { $area.hidden = true; };

  $form.onsubmit = async (e) => {
    e.preventDefault();
    $error.hidden = true;

    const code = document.getElementById('nx-inst-code').value.trim();
    const title = document.getElementById('nx-inst-title').value.trim();
    const area = document.getElementById('nx-inst-area').value.trim();
    const version = document.getElementById('nx-inst-version').value.trim();
    const paramName = document.getElementById('nx-inst-param').value.trim();
    const body = document.getElementById('nx-inst-desc').value.trim();
    const varsRaw = document.getElementById('nx-inst-vars').value.trim();
    const sql = document.getElementById('nx-inst-sql').value.trim();

    if (!code || !title) {
      $error.textContent = 'Codigo e titulo son requeridos';
      $error.hidden = false;
      return;
    }
    if (!body && !varsRaw && !sql) {
      $error.textContent = 'Indica descripcion, JSON variables_schema o sql_query';
      $error.hidden = false;
      return;
    }
    if (varsRaw) {
      try {
        JSON.parse(varsRaw);
      } catch {
        $error.textContent = 'variables_schema no es JSON valido';
        $error.hidden = false;
        return;
      }
    }

    const payload = {
      code,
      title,
      status: 'active',
      body: body || null,
      variablesSchemaJson: varsRaw || null,
      sqlQuery: sql || null,
      instructionVersion: version || null,
      paramName: paramName || null,
      area: area || null,
    };

    const result = await InstructionApiClient.create(payload);

    if (result.ok) {
      $area.hidden = true;
      $form.reset();
      const created = result.data?.data || result.data;
      if (created?.id) {
        window.location.hash = `#/instructions/${created.id}`;
        return;
      }
      await loadInstructions(1);
    } else {
      $error.textContent = result.error;
      $error.hidden = false;
    }
  };
}

async function loadInstructions(page) {
  const $list = document.getElementById('nx-inst-list');
  const $pager = document.getElementById('nx-inst-pager');
  if (!$list) return;

  $list.innerHTML = '<p class="empty-state">Cargando...</p>';

  const result = await InstructionApiClient.list(page, 15);

  if (!result.ok) {
    $list.innerHTML = `<p class="empty-state">Error: ${esc(result.error)}</p>`;
    return;
  }

  const payload = result.data?.data || result.data;
  const items = payload?.items || [];
  const totalPages = payload?.totalPages || 1;
  const pg = payload?.page || page;

  if (items.length === 0) {
    $list.innerHTML = '<p class="empty-state">No hay instrucciones en el catálogo n8n</p>';
    if ($pager) $pager.innerHTML = '';
    return;
  }

  $list.innerHTML = items.map(instructionRow).join('');

  if ($pager) {
    if (totalPages > 1) {
      let html = '';
      for (let p = 1; p <= totalPages; p++) {
        const cls = p === pg ? 'btn-primary' : 'btn-ghost';
        html += `<button class="${cls} btn-sm page-btn" data-page="${p}">${p}</button>`;
      }
      $pager.innerHTML = html;
      $pager.querySelectorAll('.page-btn').forEach(btn => {
        btn.onclick = () => loadInstructions(Number(btn.dataset.page));
      });
    } else {
      $pager.innerHTML = '';
    }
  }
}
